"use client";

import { FormEvent, ReactNode, useEffect, useState } from "react";
import { X } from "lucide-react";
import { postEnquiry } from "@/lib/db/property";
import { formatPriceShort, sanePrice } from "@/lib/v2/format";
import { setUnlocked } from "@/lib/v2/unlock";
import { reportLead } from "@/lib/metaPixel";
import { Button } from "./Button";
import { Pill } from "./Pill";
import { WhatsAppButton } from "./WhatsAppButton";

export type StickyEnquiryPanelProps = {
  propertyId: string;
  propertyName: string;
  priceFrom?: number | null;
  status: string;
  onUnlock?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  /** Sold-out projects collect alert requests instead of price-list requests. */
  soldOut?: boolean;
  /** Overrides the submit button label, e.g. "Register for the price list" on pre-launch. */
  submitLabel?: string;
};

type FormErrors = Partial<Record<"name" | "mobile" | "email" | "consent" | "submit", string>>;

const inputClass =
  "mt-1.5 h-11 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100";

function normalizeMobile(value: string): string | null {
  const compact = value.replace(/[\s()-]/g, "");
  const local = compact.replace(/^\+?65/, "");
  return /^\d{8}$/.test(local) ? `+65${local}` : null;
}

function LeadForm({
  propertyId,
  onUnlock,
  afterSubmit,
  soldOut,
  submitLabel,
}: StickyEnquiryPanelProps & { afterSubmit?: () => void }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: FormErrors = {};
    const normalizedMobile = normalizeMobile(mobile);
    if (!name.trim()) nextErrors.name = "Enter your name.";
    if (!normalizedMobile) nextErrors.mobile = "Enter a valid Singapore mobile number.";
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!consent) nextErrors.consent = "Please agree before continuing.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSending(true);
    setErrors({});
    const result = await postEnquiry({
      name: name.trim(),
      email: email.trim() || undefined,
      mobile_number: normalizedMobile as string,
      brochure: !soldOut,
      price_list: !soldOut,
      flat_type_id: [],
      property: propertyId,
      source_url: window.location.href,
    });
    setSending(false);
    if (typeof result === "string") {
      setErrors({ submit: result });
      return;
    }
    reportLead("Project enquiry panel");

    if (!soldOut) {
      setUnlocked(propertyId, {
        name: name.trim(),
        mobile: normalizedMobile as string,
        email: email.trim() || undefined,
      });
    }
    setSent(true);
    onUnlock?.();
    afterSubmit?.();
  };

  if (sent) {
    return (
      <div className="rounded-xl bg-avail-soft p-4 text-sm text-avail">
        {soldOut ? "Thanks. Winnie will send you similar launches as they open." : "Thanks. Winnie will send you the developer's latest promotion."}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      <PanelField label="Name" error={errors.name}>
        <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} autoComplete="name" />
      </PanelField>
      <PanelField label="Mobile" error={errors.mobile}>
        <input value={mobile} onChange={(event) => setMobile(event.target.value)} className={inputClass} inputMode="tel" autoComplete="tel" placeholder="+65 8877 2688" />
      </PanelField>
      <PanelField label="Email (optional)" error={errors.email}>
        <input value={email} onChange={(event) => setEmail(event.target.value)} className={inputClass} type="email" autoComplete="email" />
      </PanelField>
      <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-ink-muted">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          className="mt-0.5 size-4 rounded border-line text-brand-500 focus:ring-brand-500"
        />
        <span>I agree to be contacted about this project. My details are kept private.</span>
      </label>
      {errors.consent && <p className="text-xs text-sold">{errors.consent}</p>}
      {errors.submit && <p className="text-sm text-sold">{errors.submit}</p>}
      <Button type="submit" fullWidth disabled={sending}>
        {sending ? "Sending..." : submitLabel ?? (soldOut ? "Get alerts for similar launches" : "Get the developer's latest promotion")}
      </Button>
    </form>
  );
}

function PanelField({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="block text-sm font-medium text-ink">
      {label}
      {children}
      {error && <span className="mt-1 block text-xs font-normal text-sold">{error}</span>}
    </label>
  );
}

export function MobileEnquiryBar(props: StickyEnquiryPanelProps) {
  const [open, setOpen] = useState(false);
  // Lets the floating chat bubbles (ChatBox) lift above the bar on small screens.
  useEffect(() => {
    document.body.classList.add("has-bottom-bar");
    return () => document.body.classList.remove("has-bottom-bar");
  }, []);
  const whatsappText = `Hi Winnie, I am looking at ${props.propertyName} on AI Property`;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-line bg-white p-3 shadow-[0_-8px_30px_rgba(23,29,46,0.08)] md:hidden">
        <WhatsAppButton text={whatsappText} label="WhatsApp" size="md" />
        <Button onClick={props.onAction ?? (() => setOpen(true))} fullWidth>
          {props.actionLabel ?? "Enquire"}
        </Button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end bg-ink/50 md:hidden" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <div role="dialog" aria-modal="true" aria-labelledby="mobile-enquiry-title" className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-6">
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="absolute right-4 top-4 rounded-full p-2 text-ink-muted hover:bg-surface">
              <X aria-hidden="true" className="size-5" />
            </button>
            <Pill tone="neutral">{props.status}</Pill>
            <h2 id="mobile-enquiry-title" className="mt-3 pr-10 font-display text-2xl font-semibold tracking-tight text-ink">
              Enquire about {props.propertyName}
            </h2>
            <div className="mt-6">
              <LeadForm {...props} afterSubmit={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const StickyEnquiryBar = MobileEnquiryBar;

export function StickyEnquiryPanel(props: StickyEnquiryPanelProps) {
  const whatsappText = `Hi Winnie, I am looking at ${props.propertyName} on AI Property`;
  return (
    <>
      {/* The project page wraps this in its own sticky column wrapper, so the card itself stays static. */}
      <aside className="hidden rounded-2xl border border-line bg-white p-6 shadow-[0_8px_30px_rgba(23,29,46,0.06)] md:block">
        <Pill tone="neutral">{props.status}</Pill>
        <p className="mt-3 font-display text-xl font-semibold text-ink">{props.soldOut ? "Sold out" : props.propertyName}</p>
        {props.soldOut ? (
          <p className="mb-5 mt-1 text-sm text-ink-muted">Leave your details and Winnie tells you when a similar launch opens.</p>
        ) : (
          <p className={`mb-5 mt-1 font-display text-lg font-semibold ${sanePrice(props.priceFrom) != null ? "text-gold-600" : "text-ink-faint"}`}>
            {sanePrice(props.priceFrom) != null ? `From ${formatPriceShort(sanePrice(props.priceFrom))}` : "Price on request"}
          </p>
        )}
        <LeadForm {...props} />
        <div className="mt-3 [&>a]:w-full">
          <WhatsAppButton text={whatsappText} label="WhatsApp Winnie" />
        </div>
      </aside>
      <MobileEnquiryBar {...props} />
    </>
  );
}
